import { useState, useEffect, ChangeEvent } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridPaginationModel,
  GridRenderEditCellParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
  useGridApiContext,
} from "@mui/x-data-grid";
import {
  ProductListGridColumns,
  ProductListReferenceColumnsMapping,
} from "./ProductContstants";
import api from "../../services/ApiService";
import { ApiResponse } from "../../models/ApiResponse";
import { ItemBasicDetail } from "../../models/Items/ItemBasicDetail";
import {
    SelectedFilters,
} from "../../models/Items/ItemListRequest";
import { Permissions, PermissionsFriendlyNames, SortDirection } from "../../models/Enum";
import Filters from "./Filters";
import {
    Box,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Typography,
    FormGroup,
    IconButton,
    InputAdornment,
    TextField,
} from "@mui/material";
import Loader from "../../components/Loader";
import EditIcon from "@mui/icons-material/Edit";
import { NO_LOCAL_SEARCH_RESULTS_GRID_MESSAGE, NO_ROWS_GRID_MESSAGE } from "../../components/GridUtilityComponents";
import { toast } from "react-toastify";
import ExtractAttachmentsModal from "./ExtractAttachmentModal";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import { updateItemListRequest } from "../../reducers/itemsReducer";
import SessionService from "../../services/SessionService";
import ExportProductListing from "../../components/ExportProductListing";
import DeleteValidationModal from "../../components/ManageSettingComponents/DeleteValidationModal";
import { MULTI_SELECT_FIELD_NAMES, PRODUCT_TYPE_DROPDOWN_OPTIONS } from "./EditItem/Constants";
import { KeyValues } from "../../models/Items/FilterOptions";
import { EditItemDropdownOptions } from "../../models/Items/EditItemDropdownOptions";
import GridCellChipsContent from "../../components/GridCellChipsContent";
import { sortCVLFieldOptions } from "../../utils/helpers";
import ActionsProductListing from "../../components/ActionsProductListing";
import ConfirmationModal from "../../components/ConfirmationModal";
import moment from "moment";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import { maxRecordsWarningMessage, SAVED_PRODUCT_EXPORT_KEY } from "../../utils/constants";


export interface ProductListResponse {
  items?: ItemBasicDetail[]
  totalCount?: number
}

export const initialItemListRequest = {
  pageIndex: 1,
  pageSize: 10,
  sortBy: "id",
  sortDirection: SortDirection.DESC,
  [MULTI_SELECT_FIELD_NAMES.developmentType]: [PRODUCT_TYPE_DROPDOWN_OPTIONS[0].id],
  hideReleasedToInriver: true,
  hideArchivedDevelopment: true,
};

const formatSlIntroDateForDisplay = (value: unknown) => {
  if (!value) return "";
  const utcDate = moment.utc(value as string);
  if (!utcDate.isValid()) return "";
  return utcDate.format("MM/DD/YYYY");
};

const SlIntroDateEditCell = (props: GridRenderEditCellParams) => {
  const { id, value, row } = props;
  const apiRef = useGridApiContext();
  const [isTBD, setIsTBD] = useState<boolean>(!!row.isSLIntroDateTBD);
  const [dateValue, setDateValue] = useState<string>(
    value && !row.isSLIntroDateTBD ? moment(value).format("YYYY-MM-DD") : ""
  );

  const handleTBDChange = (checked: boolean) => {
    setIsTBD(checked);
    apiRef.current.setEditCellValue({ id, field: "isSLIntroDateTBD", value: checked });
    if (checked) {
      setDateValue("");
      apiRef.current.setEditCellValue({ id, field: "slIntroDate", value: null });
    }
  };

  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setDateValue(inputValue);
    setIsTBD(false);
    const nextValue = inputValue
      ? moment(inputValue, "YYYY-MM-DD").toDate()
      : null;
    apiRef.current.setEditCellValue({ id, field: "slIntroDate", value: nextValue });
    apiRef.current.setEditCellValue({ id, field: "isSLIntroDateTBD", value: false });
  };

  return (
    <Box sx={{ py: 0.5, width: "100%" }}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={isTBD}
            onChange={(e) => handleTBDChange(e.target.checked)}
          />
        }
        label="TBD"
        sx={{
          ml: 0,
          mr: 0,
          "& .MuiFormControlLabel-label": { fontSize: 12 },
        }}
      />
      <TextField
        type="date"
        size="small"
        fullWidth
        value={dateValue}
        disabled={isTBD}
        onChange={handleDateChange}
      />
    </Box>
  );
};

export default function ProductList() {
  const [isLoading, setLoading] = useState(false);
  const [items, setItems] = useState<ItemBasicDetail[]>([]);

  const [chooseColumnsOpen, setChooseColumnsOpen] = useState(false);
  const [exportFieldsLoading, setExportFieldsLoading] = useState(false);
  const [productLayoutExportFields, setProductLayoutExportFields] = useState<{ fieldName: string; label?: string }[]>([]);
  const [selectedExportFields, setSelectedExportFields] = useState<string[]>([]);
  const [exportFieldsSearch, setExportFieldsSearch] = useState("");

  const [openExtractAttachment, setOpenExtractAttachment] = useState(false);
  const [disableExtractAttachmentSubmit, setDisableExtractAttachmentSubmit] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allRecordsSelected, setAllRecordsSelected] = useState(false);

  const [productsTotalCount, setProductsTotalCount] = useState(0);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);

  const [deleteModalOpen, setDeleteOpenModal] = useState(false);
  const [projectId , setProjectId] = useState(0);
  const [expandedRowIds, setExpandedRowIds] = useState<number[]>([]);
  let navigate = useNavigate();

  const dispatch = useAppDispatch();

  const itemListRequestState = useAppSelector((state) => state.items.itemListRequest);

  const sessionService = SessionService.getInstance();

  const [prjStage, setPrjStage] = useState<KeyValues[]>([]);
  const [dropDownOptions, setDropDownOptions] = useState<EditItemDropdownOptions>({});
 
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const [deactivateProductModal, setDeactivateProductModal] = useState(false);
  const handleCloseDeactivateProductModal = () => {
    setDeactivateProductModal(false);
  };

  const handleDeactivateProduct = async () => {
    const data = {
      id: projectId,
      isActive: false
    };
    try {
      api
        .patch<ApiResponse<boolean>>("/item/active-deactive", data)
        .then(async (response) => {
          if (response.isSuccess) {
            toast.success(response.message);
            setDeactivateProductModal(false);
            await getItems();
          } else {
            throw new Error(response.message);
          }
        })
        .catch(() => { });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const actionColumn = {
    field: "actions",
    type: "actions",
    headerName: "Action",
    getActions: (params: GridRowParams) => {
      const { baseCode, pdStockcode, stockcode } = params?.row || {};
      const handleCopyClick = () => {
        navigate(`/copy-product`, { state: { copyProductId: params.id, copyBaseCode: baseCode, copyPdStockcode: pdStockcode, copyStockcode: stockcode, viewMode: "CopyProductDetail" } });
      };

      const actions = [
        <GridActionsCellItem
          icon={<EditIcon />}
          onClick={() => navigate(`/edit-product/${params.id}?page=${itemListRequestState.pageIndex}`)}
          label="Edit"
          sx={{ marginRight: params?.row?.baseCode?.trim() ? "0px" : "45px" }}
        />,

      ];
      
      if ((sessionService.hasPermission(Permissions.CreateProduct) || sessionService.hasPermission(Permissions.UpdateProduct)) && params?.row?.baseCode?.trim()) {
        actions.push(<Tooltip title="Copy Product" arrow>
          <GridActionsCellItem
            icon={<ContentCopyIcon />}
            onClick={handleCopyClick}
            label="Copy"
          />
        </Tooltip>);
      }
  
      return actions;
    },
  };

  const stageStatusColumn = [
    { field: "id", headerName: "Id", width: 70, disableColumnMenu: true },
    {
      field: "stockcode",
      headerName: "Stock Code",
      sortable: true,
      disableColumnMenu: true,
      width: 180
    },
    {
      field: "pdStockcode",
      headerName: "PD StockCode",
      sortable: true,
      disableColumnMenu: true,
      width: 180,
    },
    {
      field: "baseCode",
      headerName: "Base Code",
      sortable: true,
      disableColumnMenu: true,
      width: 125,
    },
    {
      field: "pdCode",
      headerName: "PD BaseCode",
      sortable: true,
      disableColumnMenu: true,
      width: 120,
    },
    {
      field: "brandCollectionDisplayText",
      headerName: "Brand Collection",
      sortable: true,
      disableColumnMenu: true,
      width: 130
    },  
    {
      field: "shortDesc",
      headerName: "Short Description",
      sortable: false,
      disableColumnMenu: true,
      width: 250
    },
    {
      field: "stageDisplayText",
      headerName: "Stage",
      sortable: true,
      disableColumnMenu: true,
      width: 120,
      editable: sessionService.hasPermission(Permissions.UpdateProduct),
      type: 'singleSelect',
      valueOptions: prjStage.map((stages) => stages.name),
    },
    {
      field: "statusDisplayText",
      headerName: "Status",
      sortable: true,
      disableColumnMenu: true,
      width: 155,
      editable: sessionService.hasPermission(Permissions.UpdateProduct),
      type: 'singleSelect',
      valueOptions: ({ row }) => {
        // Dynamically filter status options based on the stage
        const selectedStage = prjStage.find(stage => stage.name === row.stageDisplayText);
        if (!selectedStage) return [];
  
        const statusOption = dropDownOptions?.stageStatusTags
          ?.find(stage => stage.id === selectedStage.id)
          ?.statuses.map(status => status.description) || [];
  
        return statusOption;
      },
    },
    {
      field: "tagsDisplay",
      headerName: "Tags",
      sortable: false,
      disableColumnMenu: true,
      width: 350,
      renderCell: (params) => {
        const tagsWithId = Array.isArray(params.value)
          ? params.value.map((tag, index) =>
            typeof tag === "string"
              ? { id: index + 1, name: tag }
              : tag
          )
          : [];

        return (
          <GridCellChipsContent 
            keyField="id" 
            displayTextFiled="name"
            cellData={tagsWithId?.length > 3 && !expandedRowIds?.includes(params.row?.id) ? tagsWithId.filter((_, i) => i < 3) : tagsWithId}
            showViewMore={tagsWithId?.length > 3}
            viewMoreText={expandedRowIds?.includes(params.row?.id) ? "Show Less" : "Show More"}
            viewMoreHandle={() => setExpandedRowIds(expandedRowIds?.includes(params.row?.id) ? expandedRowIds.filter(itemId => itemId !== params.row?.id) : [...expandedRowIds, params.row?.id])}
          />
        )
      }
    },
    {
      field: "vendorDisplayText",
      headerName: "Vendor",
      sortable: true,
      disableColumnMenu: true,
      width: 130
    }
  ];

  const slIntroDateRow: GridColDef = {
    field: "slIntroDate",
    headerName: "SL Intro Date",
    sortable: false,
    disableColumnMenu: true,
    width: 140,
    editable: sessionService.hasPermission(Permissions.UpdateProduct),
    valueGetter: (params) => {
      if (params.row.isSLIntroDateTBD) return null;
      if (!params?.value) return null;
      const utcDate = moment.utc(params?.value);
      if (!utcDate.isValid()) return null;
      return new Date(utcDate.year(), utcDate.month(), utcDate.date());
    },
    renderCell: (params) => {
      if (params.row.isSLIntroDateTBD) return "TBD";
      return formatSlIntroDateForDisplay(params.row.slIntroDate);
    },
    renderEditCell: (params) => <SlIntroDateEditCell {...params} />,
  };

  const isSLIntroDateTBDColumn: GridColDef = {
    field: "isSLIntroDateTBD",
    editable: true,
    headerName: "",
    width: 0,
    minWidth: 0,
    maxWidth: 0,
    sortable: false,
    disableColumnMenu: true,
    filterable: false,
    disableExport: true,
    renderCell: () => null,
  };

  const handleClose = () => {
    setDeleteOpenModal(false);
  }
  
  const handleDeleteModalOpen = (params) => {
    setDeleteOpenModal(true);
    setProjectId(params.row.id);
  };

  const deleteSubmit = async () => {
    try{
      api
        .delete<ApiResponse<boolean>>(`/Item/${projectId}`)
        .then( async (response:any) => {
          if(response.isSuccess){
            toast.success(response.message);
            setDeleteOpenModal(false);
            await getItems();
          }
        })
        .catch(() => {});
    }catch(error: any) {
      toast.error(error.message);
    }
  };

  // const handleSyncSAPComponent = () => {
  //   api.post<ApiResponse<boolean>>("/component/sap-syncup", null)
  //     .then(async (response) => {
  //       if(response?.isSuccess) {
  //         toast.success(response?.message);
  //         await getItems();
  //       }
  //       else {
  //         toast.error(response?.message);
  //       }
  //     })
  //     .catch((error) => {
  //       console.error("Exception from Sync SAP Component", error);
  //     });
  // };

  const gridToolbar = () => (
    <GridToolbarContainer>
      <div style = {{marginLeft: "12px"}}>
      <Tooltip title="">
        <FormControlLabel 
          sx = {{'& .MuiTypography-root': { fontSize: '13px', fontWeight: 500 }}}          
          control={
            <Checkbox 
              checked={allRecordsSelected}
              onChange={handleSelectAll}
            />
          } 
          label={`SELECT ALL(${productsTotalCount})`}          
        />
        </Tooltip>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <ExportProductListing
          selectedRows={selectedRows}
          productsTotalCount={productsTotalCount}
          selectAll={allRecordsSelected}
          setLoading={setLoading}
          onOpenChooseColumns={() => void openChooseColumns()}
          ensureAllExportFieldsLoaded={ensureAllExportFieldNamesLoaded}
        />
        <ActionsProductListing 
          handleExtractAttachment={handleExtractAttachment}
          // handleSyncSAPComponent={handleSyncSAPComponent}
          getItems={getItems}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          allRecordsSelected={allRecordsSelected}
          setAllRecordsSelected={setAllRecordsSelected}
        /> 
      </div>
      <div style={{
        flexGrow: 1, textAlign: "center", padding: 5,
        display: "flex", justifyContent: "flex-end"
      }}>
        <GridToolbarQuickFilter sx={{ margin: "5px 30px 5px 5px", width: "30%" }} />
      </div>
    </GridToolbarContainer>
  );

  const loadSavedSelectedFields = () => {
    try {
      const raw = globalThis.localStorage.getItem(SAVED_PRODUCT_EXPORT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) return parsed as string[];
      return null;
    } catch {
      return null;
    }
  };

  const fetchProductLayoutFieldsForExport = async () => {
    setExportFieldsLoading(true);
    try {
      const resp = await api.get<ApiResponse<any[]>>("/PickList/layoutFields");
      if (resp?.isSuccess && Array.isArray(resp?.data)) {
        const flattened = resp.data
          .flatMap((cat: any) => (Array.isArray(cat?.fields) ? cat.fields : []))
          .map((f: any) => ({
            fieldName: typeof f?.fieldName === "string" ? f.fieldName.trim() : "",
            label: typeof f?.label === "string" ? f.label : undefined,
          }))
          .filter((f: any) => !!f.fieldName);

        const seen = new Set<string>();
        const unique = flattened.filter((f: any) => {
          const key = f.fieldName.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        const sorted = [...unique].sort((a: any, b: any) => {
          const al = (a.label || a.fieldName || "").toString().toLowerCase();
          const bl = (b.label || b.fieldName || "").toString().toLowerCase();
          return al.localeCompare(bl);
        });

        setProductLayoutExportFields(sorted);
        return sorted as { fieldName: string; label?: string }[];
      }
      toast.error(resp?.message || "Unable to load layout fields");
      setProductLayoutExportFields([]);
      return [];
    } catch (e) {
      console.error("Error loading layout fields", e);
      toast.error("Error loading layout fields");
      setProductLayoutExportFields([]);
      return [];
    } finally {
      setExportFieldsLoading(false);
    }
  };

  const ensureAllExportFieldNamesLoaded = async (): Promise<string[]> => {
    if (productLayoutExportFields.length > 0) return productLayoutExportFields.map((f) => f.fieldName);
    const fields = await fetchProductLayoutFieldsForExport();
    return fields.map((f) => f.fieldName);
  };

  // Prefetch once on mount so first modal open is instant
  useEffect(() => {
    void ensureAllExportFieldNamesLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openChooseColumns = async () => {
    setChooseColumnsOpen(true);
    setExportFieldsSearch("");

    const allFieldNames = await ensureAllExportFieldNamesLoaded();
    const saved = loadSavedSelectedFields();
    const normalizedAll = new Set(allFieldNames.map((x) => x.toLowerCase()));

    const selected =
      saved?.length
        ? saved.filter((x) => typeof x === "string" && normalizedAll.has(x.toLowerCase()))
        : allFieldNames;

    setSelectedExportFields(selected.length ? selected : allFieldNames);
  };

  const handleSubmitSelectedFields = () => {
    globalThis.localStorage.setItem(SAVED_PRODUCT_EXPORT_KEY, JSON.stringify(selectedExportFields));
    toast.success("Saved selected export fields");
    setChooseColumnsOpen(false);
  };

  const filteredExportFields = productLayoutExportFields.filter((f) => {
    const label = (f.label || f.fieldName || "").toString().toLowerCase();
    return label.includes(exportFieldsSearch.trim().toLowerCase());
  });

  const allExportFieldNames = productLayoutExportFields.map((f) => f.fieldName);
  const allExportFieldsSelected =
    allExportFieldNames.length > 0 &&
    allExportFieldNames.length === selectedExportFields.length &&
    allExportFieldNames.every((name) => selectedExportFields.includes(name));
  const selectAllExportIndeterminate =
    selectedExportFields.length > 0 && !allExportFieldsSelected;
  useEffect(() => {
    getAllDropDownOptions();
  },[])

  useEffect(() => {
    getItems();
  }, [itemListRequestState]);  

  const getAllDropDownOptions = async () => {
    try {
      // using same filter options that we are using in Product Listing screen + some other drop down using separate API
      const dropDownSources = await api.get<
            ApiResponse<EditItemDropdownOptions>
          >(`/Item/active-dropdown-options`);

      if (dropDownSources?.isSuccess) {
        // Sort dropdowns
        sortCVLFieldOptions(dropDownSources.data);
        
        setDropDownOptions(dropDownSources.data);
        const formattedProjectStages = dropDownSources.data.projectStages

        setPrjStage(formattedProjectStages);
      }
    } catch (error) {
      console.error("Exception from API", error);
    }
  };

  const getItems = async () => {
    try {
      setLoading(true);
      api
        .post<ApiResponse<ProductListResponse>>("/item/list", {...itemListRequestState, isActive: true})
        .then((response) => {
          setLoading(false);
          if (response.isSuccess) {
            setProductsTotalCount(response?.data?.totalCount);
            const items:ItemBasicDetail[] = (response?.data?.items || []);
            setItems(items);
            // if all rows are selected then update the selectedRows state so that grid reflects with all rows selected
            if(allRecordsSelected) {
              const itemsForSelection = items.map(i => { 
                return {
                  id: i.id,
                  stockCode: i?.stockcode,
                  baseCode: i?.baseCode
                } 
              });
             setSelectedRows(selectedRows.concat(itemsForSelection.filter(ii => !selectedRows.some(sr => sr.id === ii.id))));
            }
          } else {
            throw new Error(response.message);
          }
        })
        .catch((error) => {
          setLoading(false);
          console.error("Exception from product list", error);
        });
    } catch (error: any) {
      setLoading(false);
      console.error("Exception from product list", error);
    }
  };

  // if sorted by a field which is not direct field of product table then find the correct column from mapping
  const getSortFieldName = (gridFieldName: string): string => {
    return ProductListReferenceColumnsMapping[gridFieldName] || gridFieldName;
  };

  // convert state sortBy value to column name that is bound in grid
  const getReverseSortFieldName = (fieldName: string): string => {
    const gridFieldName = Object.keys(ProductListReferenceColumnsMapping).find(
      (prmKey) => ProductListReferenceColumnsMapping[prmKey] === fieldName
    );
    return gridFieldName || fieldName;
  };

  const onSortChange = (sorting: GridSortModel) => {
    if (sorting?.length === 0) {
        dispatch(updateItemListRequest({
            ...itemListRequestState,
            sortBy: itemListRequestState.sortBy || initialItemListRequest.sortBy,
            sortDirection: itemListRequestState?.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC,
            isActive: true
        }));
    } else {
        dispatch(updateItemListRequest({
            ...itemListRequestState,
            sortBy: getSortFieldName(sorting[0].field),
            sortDirection:
                sorting[0].sort === "asc" ? SortDirection.ASC : SortDirection.DESC,
            isActive: true
        }));
    }
  };

  const onPaginationChange = (pagination: GridPaginationModel) => {
    dispatch(updateItemListRequest({
      ...itemListRequestState,
      pageIndex: pagination.page + 1,
      pageSize: pagination.pageSize,
      isActive: true
    }));
  };

  const handleApplyFilterClick = (filterValues: SelectedFilters) => {
    if (filterValues.projectCode) {
      setSelectedRows([]);
      setAllRecordsSelected(false);
      
      dispatch(updateItemListRequest({
        ...itemListRequestState,
        projectCode: filterValues.projectCode,
        pageIndex: 1,
        isActive: true
      }));
    } else if (Object.values(filterValues)?.length) {
      setSelectedRows([]);
      setAllRecordsSelected(false);

      dispatch(updateItemListRequest({
        pageSize:itemListRequestState.pageSize, 
        sortBy: itemListRequestState.sortBy, 
        sortDirection: itemListRequestState.sortDirection,
        ...filterValues,
        pageIndex: 1,
        isActive: true
      }));
    } else {
      //reset the filters
      dispatch(updateItemListRequest({...initialItemListRequest, isActive: true}));
      setAllRecordsSelected(false);
      setSelectedRows([]);
    }
  };

  const handleCloseModal = () => {
    setOpenExtractAttachment(false);
  };
    
  const downloadFile = (response: any) => {
    if (response.isSuccess) {
        const fileName = response.data;
  
        // Open new window for download
        window.open(`${process.env.REACT_APP_API_URL}/Attachment/download-attachment-zip?fileName=${fileName}`, '_blank');
    } else {
      toast.error(response.message); 
    }
  };

  const handleSelectAll = () => {
    const allRecordsSelectedNewValue = !allRecordsSelected;
    setAllRecordsSelected(allRecordsSelectedNewValue);
    // push current page's records into the selectedRows state so that grid shows them selected.
    // Other pages will be pushed to selectedRows as and when user nagivates through pages
    if(allRecordsSelectedNewValue === true) {
      const itemIDs = items.map(i => { 
        return {
          id: i.id,
          stockCode: i?.stockcode,
          baseCode: i?.baseCode,
          pdStockCode: i?.pdStockcode
        } 
      });
      setSelectedRows(selectedRows.concat(itemIDs.filter(ii => !selectedRows.some(sr => sr.id === ii.id))));
    }
    else {
      setSelectedRows([]);
    }
  };

  const onRowSelectionModelChange = (rowIDs: GridRowId[]) => {
    // update the selectedRows state only if allRecordsSelected is false.
    if(!allRecordsSelected) {
      // NOTE: rowIDs contains IDs of all rows that were previously selected + new one that trigged this event
      //get stockCode and baseCode for the selected rows and store in state
      const selectedItems = items.filter(i => rowIDs.includes(i.id));  
      const newSelection = selectedItems.map(i => {
        return {
          id: i.id,
          stockCode: i.stockcode,
          baseCode: i.baseCode,
          pdStockCode: i.pdStockcode
        }
        //then filter the duplicate records for new selection.
      }).filter(newlySelectedItem => !selectedRows.some(sr => sr.id === newlySelectedItem.id));
      
      // if product was unchecked, remove it from previously selectedRows.
      const existingSelection = selectedRows.filter(sr => rowIDs.includes(sr.id));
      
      //set previous selection and add new selection.
      setSelectedRows([...existingSelection, ...newSelection]);
    }
  };

  const handleExtractAttachment = async () => {
    let selectedProductIds = selectedRows.map(row => row.id);
    let recordsWithoutStockCodeOrBaseCode = selectedRows.filter(row => !row.stockCode && !row.baseCode);

    if ((allRecordsSelected && productsTotalCount <= 0) || (!allRecordsSelected && selectedProductIds?.length <= 0)) {
      return toast.error("There is no Stock Code or Base Code, Please add record that have Stock Code or Base Code");
    }

    if (allRecordsSelected) {
        if (productsTotalCount > 1000) {
            return toast.error(maxRecordsWarningMessage(1000));
        } else {
          setOpenExtractAttachment(true);
        }
    } else if (!allRecordsSelected && selectedProductIds?.length > 0) {
        if (selectedProductIds?.length > 1000) {
            return toast.error(maxRecordsWarningMessage(1000));
        } else if (selectedProductIds?.length <= 1000) {
            if (recordsWithoutStockCodeOrBaseCode?.length > 0) {
                return toast.error("There are Record(s) with no Stock Code or Base Code, Please Deselect Record with no Stock Code or Base Code");
            } else {
              setOpenExtractAttachment(true);
            }
        }
    }
  };

  const handleExtractSubmit = async (itemIds, attachmentTypes) => {
    const sessionInfo: any = JSON.parse(
      window?.localStorage.getItem("session") || "{}"
    );
    try{
        setDisableExtractAttachmentSubmit(true);
        setLoading(true);
        let response = null;
        !allRecordsSelected ? response = await fetch(`${process.env.REACT_APP_API_URL}/Attachment/extract-attachments-list`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": "Bearer " + sessionInfo.token,
          },
          body: JSON.stringify({
            itemIds: itemIds,
            attachmentTypes: attachmentTypes
          })
        }) : response = await fetch(`${process.env.REACT_APP_API_URL}/Attachment/extract-attachments-with-filter`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": "Bearer " + sessionInfo.token,
          },
          body: JSON.stringify({
            filterCriteria: {...itemListRequestState, isActive: true},
            attachmentTypes: attachmentTypes
          })
        })

        if(response.ok){
          const responseBodyContent = await response.json();
        
          if(responseBodyContent.message){
            toast.error(responseBodyContent.message); 
            setLoading(false);
            setDisableExtractAttachmentSubmit(false);
          } else {
            downloadFile(responseBodyContent);
            setSelectedRows([]);
            setOpenExtractAttachment(false);
            setAllRecordsSelected(false);
            setDisableExtractAttachmentSubmit(false);
            setLoading(false);
            toast.success("Attachments Exported Successfully")
          }
        }
    }
    catch (err: any) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const handleExpandClick = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  // Handles row edit stop event and switches the row mode to "View"
  const rowEditStopHandler: GridEventListener<'rowEditStop'> = async (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut || params.field) {
      event.defaultMuiPrevented = true;
    };

    setRowModesModel((prevModel) => ({...prevModel,[params.row.id]: { mode: GridRowModes.View}}));
  };

  // Updates the row modes model state with the provided new row modes model
  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  /*
    Handles double-click on a cell to enable row edit mode for specific fields ('stage' or 'status')
    Ensures all other rows are set to "View" mode before enabling "Edit" mode for the clicked row
  */
  const handleCellDoubleClick = async (params) => {
    if (params.field === 'stageDisplayText' || params.field === 'statusDisplayText' || params.field === 'slIntroDate') {
      if (params.field && rowModesModel[params.row.id]?.mode !== GridRowModes.Edit) {

        setRowModesModel((prevModel) => {
          const newModel = { ...prevModel };

          // Set all rows to "View" mode
          Object.keys(newModel).forEach((rowId) => {
            newModel[rowId] = { mode: GridRowModes.View };
          });

          // Set the clicked row to "Edit" mode
          newModel[params.row.id] = { mode: GridRowModes.Edit };
          return newModel;
        });
      };
    };
  };

  // Processes the row update by handling changes made to a row's data
  const processRowUpdate = async (newRow: GridRowModel, oldRow: GridRowModel) => {
    // Find the selected stage based on the stage display text from the row
    const selectedStage = prjStage.find((stage) => stage.name === newRow.stageDisplayText);
    // Get the stage ID, or null if the stage is not found
    const stageId = selectedStage ? selectedStage.id : null;

    // Find the selected status based on the stage ID and status description
    const selectedStatus = dropDownOptions?.stageStatusTags
      ?.find(stage => stage.id === selectedStage?.id)
      ?.statuses.find(status => status.description === newRow.statusDisplayText);
    // Get the status ID, or null if the status is not found
    const statusId = selectedStatus ? selectedStatus.id : null;

    // Return false due to when the user update stage only and save it keep changing the UI
    if (!stageId || !statusId) {
      setRowModesModel((prevModel) => ({ ...prevModel, [newRow.id]: { mode: GridRowModes.View, ignoreModifications: true } }));
      toast.error("Please Select Both Stage and Status to Update");
      return false;
    };

    const newIsTBD = !!newRow.isSLIntroDateTBD;
    const oldIsTBD = !!oldRow.isSLIntroDateTBD;
    const newSlIntroDate = newIsTBD
      ? null
      : (newRow?.slIntroDate
        ? moment(newRow.slIntroDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss")
        : null);
    const oldSlIntroDate = oldIsTBD
      ? null
      : (oldRow?.slIntroDate
        ? moment(oldRow.slIntroDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss")
        : null);

    // Prepare the modified row data
    const modifiedRowData = {
      ...newRow,
      stage: stageId,
      status: statusId,
      stageDisplayText: newRow.stageDisplayText,
      isSLIntroDateTBD: newIsTBD,
      slIntroDate: newSlIntroDate,
    };

    // Check if the stage, status, or SL intro date has changed before making an API call
    if (
      newRow.stage !== stageId
      || newRow.status !== statusId
      || oldSlIntroDate !== newSlIntroDate
      || newIsTBD !== oldIsTBD
    ) {
      try {
        // If the stage or status has changed, update the row data via API
        await updateRowApi(modifiedRowData);
        // Once the update is successful, switch the row back to 'View' mode
        setRowModesModel((prevModel) => ({ ...prevModel, [newRow.id]: { mode: GridRowModes.View } }));
        // Return the modified row data after update
        return modifiedRowData;

      } catch (error) {
        console.error("Error updating row:", error);
        // In case of an error, switch the row back to 'View' mode without applying changes
        setRowModesModel((prevModel) => ({ ...prevModel, [newRow.id]: { mode: GridRowModes.View } }));
        // Return the original row if update failed
        return {
          ...newRow,
          slIntroDate: oldRow.slIntroDate,
          isSLIntroDateTBD: oldIsTBD,
        };
      };
    } else {
      // If no changes were made, switch the row back to 'View' mode and ignore modifications
      setRowModesModel((prevModel) => ({ ...prevModel, [newRow.id]: { mode: GridRowModes.View, ignoreModifications: true } }))
      // Return the original row without modifications
      return {
        ...newRow,
        slIntroDate: oldRow.slIntroDate,
        isSLIntroDateTBD: oldIsTBD,
      };
    };
  };

  // Function to update product details with Row Edit via API call
  const updateRowApi = async (rowData) => {
    
    // Prepare the request payload with the row data
    const requestPayload = {
      id: rowData.id,
      stage: rowData.stage,
      status: rowData.status,
      slIntroDate: rowData.isSLIntroDateTBD ? null : rowData.slIntroDate,
      isSLIntroDateTBD: !!rowData.isSLIntroDateTBD,
    };

    try {
      api.put<ApiResponse<number>>('/Item/update-item-basic-detail', requestPayload)
        .then((response) => {
          if (response?.isSuccess) {
            toast.success(response.message);
          }
        })
        .catch((error) => console.error("Exception from Update Product detail API", error));
    } catch (err: any) {
      toast.error(err?.message);
    };
  };
  
  return (
    <>
      <ConfirmationModal
        isOpen={deactivateProductModal}
        onClose={handleCloseDeactivateProductModal}
        title={"Confirm Deactivate Product"}
        message={"Are you sure you want to deactivate this product?"}
        handleConfirmYes={handleDeactivateProduct}
        handleConfirmNo={handleCloseDeactivateProductModal}
      />
      <DeleteValidationModal 
        addModalOpen={deleteModalOpen} 
        handleCloseModal={handleClose} 
        deleteSubmit={deleteSubmit} 
        fieldIdentifier={`project #${projectId}`} 
      />
      {openExtractAttachment && <ExtractAttachmentsModal
        isOpen={openExtractAttachment}
        onClose={handleCloseModal}
        selectedItems={selectedRows}
        onSubmit={handleExtractSubmit}
        allSelected={allRecordsSelected}
        disableExtractAttachmentSubmit={disableExtractAttachmentSubmit}
        isLoading={isLoading}
      />
      }
      <Filters
        onApplyFilter={handleApplyFilterClick}
        filtersExpanded={filtersExpanded}
        handleExpandClick={handleExpandClick}
        viewMode={PermissionsFriendlyNames[Permissions.ViewProducts]}
      />
      <Loader isLoading={isLoading} />
      <Box
        sx={{
          height: "70%",
          width: "100%",
          display: "block",
        }}
      >
        <DataGrid
          rows={items}
          columns={[actionColumn, ...stageStatusColumn,...ProductListGridColumns, slIntroDateRow, isSLIntroDateTBDColumn,
              ...(sessionService.hasPermission(Permissions.ActivateDeactivateProduct) ? [{
                field: "isActive", sortable: false, disableColumnMenu: true, headerName: "Is Active", width: 100,
                renderCell: ({ row }) =>
                  <Switch
                    checked={row?.isActive}
                    onChange={async (e) => {
                      setProjectId(row?.id);
                      setDeactivateProductModal(true);
                    }}
                    color="primary"
                    inputProps={{ "aria-label": "controlled" }}
                  />
            }] : [])]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10
              },
            },
          }}
          rowCount={productsTotalCount}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick={true}
          disableColumnFilter={true}
          sortingMode="server"
          paginationMode="server"
          sortModel={[
            {
              field: getReverseSortFieldName(itemListRequestState.sortBy),
              sort:
              itemListRequestState.sortDirection == SortDirection.ASC
                  ? "asc"
                  : "desc",
            },
          ]}
          onSortModelChange={onSortChange}
          onPaginationModelChange={onPaginationChange}
          paginationModel={{
            page: itemListRequestState.pageIndex - 1, 
            pageSize: itemListRequestState.pageSize,
          }}
          slots={{
            toolbar: gridToolbar,
            noRowsOverlay: () => NO_ROWS_GRID_MESSAGE,
            noResultsOverlay: () => NO_LOCAL_SEARCH_RESULTS_GRID_MESSAGE,
          }}
          checkboxSelection={true}
          isRowSelectable={(params: GridRowParams) => (!allRecordsSelected) }
          onRowSelectionModelChange={onRowSelectionModelChange}
          rowSelectionModel={selectedRows.map(sr => sr.id)}
          //to keep selection from previous page
          keepNonExistentRowsSelected
          hideFooterSelectedRowCount = {allRecordsSelected}
          getRowHeight = {()=> 'auto'}
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={rowEditStopHandler}
          editMode="row"
          onCellDoubleClick={handleCellDoubleClick}
          processRowUpdate={processRowUpdate}
          columnVisibilityModel={{ isSLIntroDateTBD: false }}
          // columnVisibilityModel={{
          //   actions: (sessionService.hasPermission(Permissions.ProductUpdateFullAccess)||
          //   (sessionService.hasPermission(Permissions.ManageAttachments)))
          // }}
        />
      </Box>

      <Dialog
        open={chooseColumnsOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setChooseColumnsOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
          <span>Choose Columns to Export</span>
          <IconButton
            size="small"
            aria-label="Close"
            onClick={() => setChooseColumnsOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder="Search fields…"
            value={exportFieldsSearch}
            onChange={(e) => setExportFieldsSearch(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              endAdornment: exportFieldsSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setExportFieldsSearch("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          {exportFieldsLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading fields…</Typography>
            </Box>
          ) : productLayoutExportFields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No fields found.
            </Typography>
          ) : filteredExportFields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No matching fields.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 420, overflow: "auto" }}>
              <FormGroup>
                {filteredExportFields.map((field) => {
                  const checked = selectedExportFields.includes(field.fieldName);
                  return (
                    <FormControlLabel
                      key={field.fieldName}
                      control={
                        <Switch
                          checked={checked}
                          onChange={(e) => {
                            setSelectedExportFields((prev) => {
                              if (e.target.checked) return [...prev, field.fieldName];
                              return prev.filter((x) => x !== field.fieldName);
                            });
                          }}
                        />
                      }
                      label={field.label || field.fieldName}
                    />
                  );
                })}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <FormControlLabel
            sx={{
              marginLeft: "0",
              paddingRight: "10px",
              borderRadius: "5px",
              backgroundColor: "#454545",
              "& .MuiTypography-root": {
                color: "#ffffff"
              }
            }}
            control={
              <Checkbox
                sx={{
                  color: "#ffffff",
                  "&.MuiCheckbox-root": {
                    color: "#ffffff"
                  },
                  "&.Mui-checked": {
                    color: "#ffffff"
                  }
                }}
                checked={allExportFieldsSelected}
                indeterminate={selectAllExportIndeterminate}
                disabled={productLayoutExportFields.length === 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedExportFields(allExportFieldNames);
                  } else {
                    setSelectedExportFields([]);
                  }
                }}
              />
            }
            label="Select All"
          />
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleSubmitSelectedFields}
            variant="contained"
            disabled={productLayoutExportFields.length === 0 || selectedExportFields.length === 0}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
